/**
 * Phone Change Notice (plan Ф3б, decision 62)
 *
 * After the number changes through updatePlayerInfo, the deposit keeps sending the previous
 * number to the provider for about ten minutes. On cards whose schema writes the number
 * (`system` + `target: mobilePhone`) this says so — a) a notice while the window runs — and
 * b) asks before paying with a number changed in the form itself.
 *
 * Inert without `data-phone-change-notice` on the modal: the server emits it only when the
 * flag is on and a card writes the number, so with the flag off nothing here runs at all.
 *
 * Time left comes from the server (its clock); this only counts it down from page load on
 * the monotonic clock, so a wrong clock in the browser can shift the shown HH:MM but never
 * decide whether the notice shows.
 *
 * @package betbeast
 */

const digits = (value) => String(value ?? '').replace(/\D/g, '');

export class PhoneChangeNotice {
    /**
     * @param {HTMLElement} modalElement
     * @param {Object} schemaRenderer SchemaRenderer of the deposit flow
     * @param {Object} [clock] { now(): ms monotonic, wall(): ms epoch } — injectable for tests
     */
    constructor(modalElement, schemaRenderer, clock = null) {
        this.modal = modalElement;
        this.renderer = schemaRenderer;
        this.clock = clock || { now: () => performance.now(), wall: () => Date.now() };
        this.config = PhoneChangeNotice.readConfig(modalElement);
        this.loadedAt = this.clock.now();
        this.timer = null;

        if (this.config && this.renderer) {
            this.renderer.onRender = () => this.refresh();
        }
    }

    /**
     * The modal's config, or null when the feature must stay invisible.
     *
     * @param {HTMLElement|null} modalElement
     * @returns {Object|null}
     */
    static readConfig(modalElement) {
        const raw = modalElement?.dataset?.phoneChangeNotice;
        if (!raw) return null;
        try {
            const c = JSON.parse(raw);
            if (!c || typeof c !== 'object' || !c.texts || typeof c.target !== 'string') return null;
            return {
                target: c.target,
                window: Number.isInteger(c.window) && c.window > 0 ? c.window : 0,
                remaining: Number.isFinite(c.remaining) && c.remaining > 0 ? c.remaining : 0,
                texts: c.texts,
            };
        } catch (e) {
            return null;
        }
    }

    get active() {
        return this.config !== null;
    }

    /** The rendered field that writes the number, or null. */
    numberField() {
        const fields = this.renderer?.schema?.fields ?? [];
        return fields.find((f) => f && f.system && f.target === this.config?.target) || null;
    }

    /** Milliseconds of the window left, counted from page load on the monotonic clock. */
    leftMs() {
        if (!this.active || this.config.window <= 0 || this.config.remaining <= 0) return 0;
        return Math.max(0, this.config.remaining * 1000 - (this.clock.now() - this.loadedAt));
    }

    /** a) After every render of the fields: show or drop the "recent change" notice. */
    refresh() {
        if (!this.active) return;
        const container = this.renderer?.container;
        if (!container) return;

        container.querySelectorAll('[data-phone-change-notice]').forEach((n) => n.remove());
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }

        const field = this.numberField();
        const left = this.leftMs();
        if (!field || left <= 0) return;

        const wrapper = container.querySelector(`[data-schema-field-wrapper="${field.code}"]`);
        if (!wrapper) return;

        const at = new Date(this.clock.wall() + left);
        const hhmm = at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const notice = document.createElement('p');
        notice.className = 'wallet-deposit-phone-notice';
        notice.setAttribute('data-phone-change-notice', 'recent');
        notice.textContent = String(this.config.texts.recent || '').replace('%s', hhmm);
        // After the field's error, which stays right under the field.
        const anchor = wrapper._errorElement?.isConnected ? wrapper._errorElement : wrapper;
        anchor.parentNode.insertBefore(notice, anchor.nextSibling);

        this.timer = setTimeout(() => notice.remove(), left);
    }

    /**
     * b) Before submit: the player changed the number in the form → ask. Resolves true to go
     * on, false to stay in the form. Always true when inactive, when the card has no such
     * field, or when there was no pre-filled number to differ from.
     *
     * @returns {Promise<boolean>}
     */
    confirmIfNumberChanged() {
        if (!this.active) return Promise.resolve(true);
        const field = this.numberField();
        if (!field) return Promise.resolve(true);

        // The field's value as the renderer sends it ("+1…"), not the input: the phone field
        // shows the masked national number, whose digits never equal the stored ones.
        const before = typeof this.renderer._initialFor === 'function' ? this.renderer._initialFor(field) : '';
        const now = typeof this.renderer._readValue === 'function' ? (this.renderer._readValue(field) ?? '') : '';
        if (digits(before) === '' || digits(now) === digits(before)) return Promise.resolve(true);

        return this.ask();
    }

    /** The inline confirmation, placed above the submit button. */
    ask() {
        const submit = this.modal.querySelector('[data-action="initiate-deposit"]');
        const anchor = submit?.parentNode;
        if (!anchor) return Promise.resolve(true);

        this.modal.querySelectorAll('[data-phone-change-confirm]').forEach((n) => n.remove());

        const t = this.config.texts;
        const text = this.config.window > 0
            ? String(t.confirm_window || t.confirm || '').replace('%d', String(this.config.window))
            : String(t.confirm || '');

        const box = document.createElement('div');
        box.className = 'wallet-deposit-phone-confirm';
        box.setAttribute('data-phone-change-confirm', '');
        box.setAttribute('role', 'alertdialog');

        const p = document.createElement('p');
        p.className = 'wallet-deposit-phone-confirm__text';
        p.textContent = text;

        const go = document.createElement('button');
        go.type = 'button';
        go.className = 'wallet-deposit-phone-confirm__continue';
        go.textContent = t.continue || 'Continue';

        const stop = document.createElement('button');
        stop.type = 'button';
        stop.className = 'wallet-deposit-phone-confirm__cancel';
        stop.textContent = t.cancel || 'Cancel';

        box.append(p, go, stop);
        anchor.insertBefore(box, submit);
        if (submit) submit.style.display = 'none';

        return new Promise((resolve) => {
            const done = (answer) => {
                box.remove();
                if (submit) submit.style.display = '';
                resolve(answer);
            };
            go.addEventListener('click', () => done(true));
            stop.addEventListener('click', () => done(false));
            go.focus?.();
        });
    }
}
